// UI text of the Copilot admin inbox (Arabic / English). Category labels come
// from the backend (/copilot/meta); the customer widget's texts stay in
// components/copilot/copilotText.js.

export const ADMIN_TEXT = {
  en: {
    title: "LUD Copilot",
    subtitle: "Support inbox",
    stats: {
      unresolved: "Unresolved",
      unresolvedHint: "Open + pending conversations",
      unassigned: "Unassigned",
      pending: "Pending",
      resolved: "Resolved",
      unread: "Unread",
      unreadHint: "Conversations with customer messages nobody has read yet (not the same as unresolved)",
      regionLabel: "Conversation statistics",
    },
    search: "Search subject, reference, name or email",
    filters: "Filters",
    clearFilters: "Clear filters",
    status: "Status",
    allStatuses: "All statuses",
    unresolvedStatuses: "Unresolved (open + pending)",
    statusNames: { open: "Open", pending: "Pending", resolved: "Resolved" },
    statusHints: { open: "Waiting for support", pending: "Waiting for the customer", resolved: "Closed" },
    category: "Category",
    allCategories: "All categories",
    assignment: "Assignment",
    anyone: "Anyone",
    mine: "Assigned to me",
    unassignedOption: "Unassigned",
    byAgent: "Assigned to…",
    from: "From",
    to: "To",
    onlyUnread: "Unread only",
    conversations: "Conversations",
    loading: "Loading…",
    loadMore: "Load more",
    loadEarlier: "Load earlier messages",
    retry: "Try again",
    empty: "No conversations match",
    emptyFiltered: "No conversations match these filters.",
    emptyAll: "No conversations yet.",
    listError: "Could not load the conversations.",
    detailError: "Could not load this conversation.",
    notFound: "This conversation is not available to you.",
    selectOne: "Select a conversation",
    selectOneBody: "Pick a conversation from the list to read and reply.",
    back: "Back to the list",
    details: "Details",
    closeDetails: "Close details",
    refresh: "Refresh",
    customer: "Customer",
    registeredUser: "Registered user",
    guest: "Guest",
    name: "Name",
    email: "Email",
    phone: "Phone",
    notProvided: "Not provided",
    conversation: "Conversation",
    reference: "Reference",
    created: "Created",
    lastActivity: "Last activity",
    firstResponse: "First response",
    relatedFaq: "Related FAQ",
    reopened: (n) => `Reopened ${n}×`,
    restaurant: "Restaurant",
    branch: "Branch",
    none: "—",
    assignedTo: "Assigned to",
    unassignedLabel: "Unassigned",
    assign: "Assign",
    reassign: "Reassign",
    assignTitle: "Assign conversation",
    assignBody: "Choose the support agent who will handle this conversation.",
    assignee: "Agent",
    unassignOption: "Nobody (unassign)",
    loadingAgents: "Loading agents…",
    agentsError: "Could not load the agents.",
    save: "Save",
    cancel: "Cancel",
    resolution: "Resolution",
    resolvedBy: "Resolved by",
    resolvedAt: "Resolved on",
    note: "Note",
    changeStatus: "Change status",
    markPending: "Mark pending",
    markOpen: "Mark open",
    resolve: "Resolve",
    reopen: "Reopen",
    resolveTitle: "Resolve conversation",
    resolveBody: "The customer will see that the conversation is resolved. They can reopen it by writing again.",
    resolveNote: "Resolution note (optional, staff only)",
    copy: "Copy",
    copied: "Copied",
    copyFailed: "Could not copy",
    reply: "Reply",
    internalNote: "Internal note",
    composerReply: "Write a reply to the customer…",
    composerNote: "Write an internal note (the customer cannot see it)…",
    composerLabel: "Message",
    composerModes: "Message type",
    send: "Send",
    sendNote: "Add note",
    sending: "Sending…",
    failed: "Not sent",
    retrySend: "Retry",
    discard: "Discard",
    attach: "Attach files",
    removeFile: "Remove file",
    resolvedNoReply: "This conversation is resolved. Reopen it to reply to the customer; internal notes are still allowed.",
    internalBadge: "Internal note",
    visibleToCustomer: "Visible to the customer",
    staffOnly: "Staff only",
    you: "You",
    customerLabel: "Customer",
    system: "System",
    newMessages: "New messages",
    unreadBadge: (n) => `${n} unread`,
    unreadLabel: (n) => (n === 1 ? "1 unread message" : `${n} unread messages`),
    offline: "No connection. Check your internet and try again.",
    forbidden: "You do not have permission to do that.",
    rateLimited: (s) => `Too many requests. Try again in ${s || 30} seconds.`,
    conflict: "This conversation changed meanwhile. It was refreshed; try again.",
    serverError: "Something went wrong. Please try again.",
    actionDone: { assigned: "Assigned", unassigned: "Unassigned", status: "Status updated", note: "Note added" },
    viewList: "Conversation list",
    viewChat: "Conversation",
    event: "Update",
    assignedAgentBadge: "Agent",
    close: "Close",
    analytics: {
      button: "Analytics",
      title: "Support analytics",
      noData: "No data yet",
      responseTimes: "Response times",
      firstResponse: "Average first response",
      resolution: "Average resolution time",
      last7: "Last 7 days",
      createdResolved: (c, r) => `${c} new / ${r} resolved`,
      window: "Averages cover the last 30 days.",
      openPending: "Open, pending and unassigned",
      byCategory: "Unresolved by category",
      byAgent: "Unresolved by agent",
      perDay: "New conversations, last 14 days",
      minutes: (m) => `${m} min`,
      hours: (h) => `${h} h`,
      hoursMinutes: (h, m) => `${h} h ${m} min`,
      agentSplit: (o, p) => `${o} open, ${p} pending`,
    },
    savedReplies: {
      button: "Saved replies",
      title: "Saved replies",
      new: "New reply",
      search: "Search saved replies",
      insert: "Insert",
      edit: "Edit",
      archive: "Archive",
      empty: "No saved replies yet.",
      noMatch: "No saved reply matches.",
      fieldTitle: "Title",
      fieldBody: "Reply text",
      fieldLang: "Language",
      langAny: "Any language",
      langAll: "All languages",
      placeholders: "You can use {{customer_name}}, {{reference}} and {{agent_name}}; they are filled in when the reply is inserted.",
      titleRequired: "Enter a title (at least 2 characters)",
      bodyRequired: "Enter the reply text",
      bodyLong: "The reply must be at most 5000 characters",
      saved: "Saved",
      archived: "Archived",
    },
  },
  ar: {
    title: "مساعد لُد",
    subtitle: "صندوق الدعم",
    stats: {
      unresolved: "غير محلولة",
      unresolvedHint: "المحادثات المفتوحة + المعلّقة",
      unassigned: "غير مسندة",
      pending: "معلّقة",
      resolved: "محلولة",
      unread: "غير مقروءة",
      unreadHint: "محادثات فيها رسائل عملاء لم يقرأها أحد (وليست هي نفسها غير المحلولة)",
      regionLabel: "إحصاءات المحادثات",
    },
    search: "ابحث بالموضوع أو المرجع أو الاسم أو البريد",
    filters: "التصفية",
    clearFilters: "مسح التصفية",
    status: "الحالة",
    allStatuses: "كل الحالات",
    unresolvedStatuses: "غير محلولة (مفتوحة + معلّقة)",
    statusNames: { open: "مفتوحة", pending: "معلّقة", resolved: "محلولة" },
    statusHints: { open: "بانتظار الدعم", pending: "بانتظار العميل", resolved: "مغلقة" },
    category: "التصنيف",
    allCategories: "كل التصنيفات",
    assignment: "الإسناد",
    anyone: "الجميع",
    mine: "المسندة لي",
    unassignedOption: "غير مسندة",
    byAgent: "مسندة إلى…",
    from: "من",
    to: "إلى",
    onlyUnread: "غير المقروءة فقط",
    conversations: "المحادثات",
    loading: "جارٍ التحميل…",
    loadMore: "عرض المزيد",
    loadEarlier: "عرض الرسائل الأقدم",
    retry: "إعادة المحاولة",
    empty: "لا توجد محادثات مطابقة",
    emptyFiltered: "لا توجد محادثات تطابق هذه التصفية.",
    emptyAll: "لا توجد محادثات بعد.",
    listError: "تعذّر تحميل المحادثات.",
    detailError: "تعذّر تحميل هذه المحادثة.",
    notFound: "هذه المحادثة غير متاحة لك.",
    selectOne: "اختر محادثة",
    selectOneBody: "اختر محادثة من القائمة لقراءتها والرد عليها.",
    back: "العودة إلى القائمة",
    details: "التفاصيل",
    closeDetails: "إغلاق التفاصيل",
    refresh: "تحديث",
    customer: "العميل",
    registeredUser: "مستخدم مسجّل",
    guest: "زائر",
    name: "الاسم",
    email: "البريد الإلكتروني",
    phone: "الجوال",
    notProvided: "غير متوفر",
    conversation: "المحادثة",
    reference: "المرجع",
    created: "تاريخ الإنشاء",
    lastActivity: "آخر نشاط",
    firstResponse: "أول رد",
    relatedFaq: "السؤال الشائع المرتبط",
    reopened: (n) => `أُعيد فتحها ${n} مرة`,
    restaurant: "المطعم",
    branch: "الفرع",
    none: "—",
    assignedTo: "مسندة إلى",
    unassignedLabel: "غير مسندة",
    assign: "إسناد",
    reassign: "إعادة إسناد",
    assignTitle: "إسناد المحادثة",
    assignBody: "اختر وكيل الدعم الذي سيتولى هذه المحادثة.",
    assignee: "الوكيل",
    unassignOption: "لا أحد (إلغاء الإسناد)",
    loadingAgents: "جارٍ تحميل الوكلاء…",
    agentsError: "تعذّر تحميل الوكلاء.",
    save: "حفظ",
    cancel: "إلغاء",
    resolution: "الحل",
    resolvedBy: "تم الحل بواسطة",
    resolvedAt: "تاريخ الحل",
    note: "ملاحظة",
    changeStatus: "تغيير الحالة",
    markPending: "تعليق (بانتظار العميل)",
    markOpen: "جعلها مفتوحة",
    resolve: "حل",
    reopen: "إعادة فتح",
    resolveTitle: "حل المحادثة",
    resolveBody: "سيرى العميل أن المحادثة تم حلها، ويمكنه إعادة فتحها بالكتابة مرة أخرى.",
    resolveNote: "ملاحظة الحل (اختيارية، للموظفين فقط)",
    copy: "نسخ",
    copied: "تم النسخ",
    copyFailed: "تعذّر النسخ",
    reply: "رد",
    internalNote: "ملاحظة داخلية",
    composerReply: "اكتب ردًا للعميل…",
    composerNote: "اكتب ملاحظة داخلية (لا يراها العميل)…",
    composerLabel: "الرسالة",
    composerModes: "نوع الرسالة",
    send: "إرسال",
    sendNote: "إضافة ملاحظة",
    sending: "جارٍ الإرسال…",
    failed: "لم تُرسل",
    retrySend: "إعادة الإرسال",
    discard: "حذف",
    attach: "إرفاق ملفات",
    removeFile: "إزالة الملف",
    resolvedNoReply: "هذه المحادثة محلولة. أعد فتحها للرد على العميل؛ الملاحظات الداخلية ما زالت متاحة.",
    internalBadge: "ملاحظة داخلية",
    visibleToCustomer: "ظاهرة للعميل",
    staffOnly: "للموظفين فقط",
    you: "أنت",
    customerLabel: "العميل",
    system: "النظام",
    newMessages: "رسائل جديدة",
    unreadBadge: (n) => `${n} غير مقروءة`,
    unreadLabel: (n) => (n === 1 ? "رسالة غير مقروءة" : `${n} رسائل غير مقروءة`),
    offline: "لا يوجد اتصال. تحقق من الإنترنت وحاول مرة أخرى.",
    forbidden: "ليست لديك صلاحية لتنفيذ ذلك.",
    rateLimited: (s) => `طلبات كثيرة. حاول مرة أخرى بعد ${s || 30} ثانية.`,
    conflict: "تغيّرت المحادثة في هذه الأثناء. تم تحديثها؛ حاول مرة أخرى.",
    serverError: "حدث خطأ ما. حاول مرة أخرى.",
    actionDone: { assigned: "تم الإسناد", unassigned: "تم إلغاء الإسناد", status: "تم تحديث الحالة", note: "تمت إضافة الملاحظة" },
    viewList: "قائمة المحادثات",
    viewChat: "المحادثة",
    event: "تحديث",
    assignedAgentBadge: "وكيل",
    close: "إغلاق",
    analytics: {
      button: "التحليلات",
      title: "تحليلات الدعم",
      noData: "لا توجد بيانات بعد",
      responseTimes: "أوقات الاستجابة",
      firstResponse: "متوسط أول رد",
      resolution: "متوسط وقت الحل",
      last7: "آخر 7 أيام",
      createdResolved: (c, r) => `${c} جديدة / ${r} محلولة`,
      window: "المتوسطات تغطي آخر 30 يومًا.",
      openPending: "مفتوحة ومعلّقة وغير مسندة",
      byCategory: "غير المحلولة حسب التصنيف",
      byAgent: "غير المحلولة حسب الوكيل",
      perDay: "المحادثات الجديدة، آخر 14 يومًا",
      minutes: (m) => `${m} د`,
      hours: (h) => `${h} س`,
      hoursMinutes: (h, m) => `${h} س ${m} د`,
      agentSplit: (o, p) => `${o} مفتوحة، ${p} معلّقة`,
    },
    savedReplies: {
      button: "ردود جاهزة",
      title: "الردود الجاهزة",
      new: "رد جديد",
      search: "ابحث في الردود الجاهزة",
      insert: "إدراج",
      edit: "تعديل",
      archive: "أرشفة",
      empty: "لا توجد ردود جاهزة بعد.",
      noMatch: "لا يوجد رد جاهز مطابق.",
      fieldTitle: "العنوان",
      fieldBody: "نص الرد",
      fieldLang: "اللغة",
      langAny: "أي لغة",
      langAll: "كل اللغات",
      placeholders: "يمكنك استخدام {{customer_name}} و{{reference}} و{{agent_name}}؛ تُستبدل عند إدراج الرد.",
      titleRequired: "أدخل عنوانًا (حرفان على الأقل)",
      bodyRequired: "أدخل نص الرد",
      bodyLong: "يجب ألا يزيد الرد عن 5000 حرف",
      saved: "تم الحفظ",
      archived: "تمت الأرشفة",
    },
  },
};

// Event notices written by the backend (English); shown translated.
const EVENT_AR = {
  "Conversation resolved": "تم حل المحادثة",
  "Conversation reopened": "تمت إعادة فتح المحادثة",
  Unassigned: "أُلغي الإسناد",
};
export function eventLabel(text, lang) {
  if (lang !== "ar") return text;
  if (EVENT_AR[text]) return EVENT_AR[text];
  const assigned = /^Assigned to (.+?)( \(first reply\))?$/.exec(text);
  if (assigned) return `أُسندت إلى ${assigned[1]}${assigned[2] ? " (أول رد)" : ""}`;
  const changed = /^Status changed from (\w+) to (\w+)$/.exec(text);
  if (changed) return `تغيّرت الحالة من ${ADMIN_TEXT.ar.statusNames[changed[1]] || changed[1]} إلى ${ADMIN_TEXT.ar.statusNames[changed[2]] || changed[2]}`;
  return text;
}

export function adminErrorMessage(error, t) {
  if (error?.network) return t.offline;
  if (error?.status === 403) return t.forbidden;
  if (error?.status === 429) return t.rateLimited(error.retryAfter);
  if (error?.status === 409) {
    if (error.code === "CONVERSATION_RESOLVED") return t.resolvedNoReply;
    return t.conflict;
  }
  if (error?.status === 400 && error.message) return error.message;
  return t.serverError;
}
